import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "../../lib/utils";

type AvatarVariant = "default" | "operator" | "persona";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & { variant?: AvatarVariant }
>(({ className, variant = "default", ...props }, ref) => {
  /*
   * Avatar — Clones Design System.
   *
   * The "persona" variant carries a dashed terracotta border. The
   * handoff also defines an animated halo via ::after but Tailwind's
   * preflight zeroes pseudo-element borders, so we ship just the
   * stable dashed outline here (no halo) and let chat surfaces wire
   * the halo explicitly via the .c-avatar--persona class when they
   * want it.
   */
  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
        variant === "default" && "bg-[var(--paper-3)] text-[var(--ink)]",
        variant === "operator" && "bg-[var(--ink)] text-[var(--paper)]",
        variant === "persona" &&
          "border border-dashed border-[var(--persona)] bg-[var(--paper)] text-[var(--ink)]",
        className,
      )}
      {...props}
    />
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
